import React from 'react';
import { Star } from 'lucide-react';
import TrendingReasonChip from '../TrendingReasonChip';

const TMDB_LOGO_BASE = 'https://image.tmdb.org/t/p/w45';

const ShowDenseCard = ({ show, rank, onOpen }) => {
  return (
    <article
      className="dense-card"
      data-testid={`dense-card-${show.tmdb_id}`}
      onClick={() => onOpen(show)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(show);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="rank">#{rank}</div>
      <div className="poster">
        {show.image_url ? (
          <img src={show.image_url} alt={`${show.title} poster`} loading="lazy" />
        ) : (
          <div className="poster-fallback">No poster</div>
        )}
      </div>
      <div className="body">
        <h3 className="title">{show.title}</h3>
        <div className="meta">
          {show.year && <span>{show.year}</span>}
          {show.year && show.vote_average ? <span className="dot">•</span> : null}
          {show.vote_average ? (
            <span className="score">
              <Star size={12} /> {show.vote_average.toFixed(1)}
            </span>
          ) : null}
        </div>
        {show.trending_reason && (
          <div className="dense-reason">
            <TrendingReasonChip reason={show.trending_reason} size="sm" />
          </div>
        )}
        <p className="dense-overview">{show.overview}</p>
        {show.providers && show.providers.length > 0 && (
          <div className="providers" data-testid={`providers-${show.tmdb_id}`}>
            {show.providers.slice(0, 4).map((p) => (
              <span key={p.provider_id} title={p.name} className="provider">
                {p.logo_path ? (
                  <img
                    src={`${TMDB_LOGO_BASE}${p.logo_path}`}
                    alt={p.name}
                    width="18"
                    height="18"
                    loading="lazy"
                  />
                ) : (
                  <span className="provider-text">{p.name}</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .dense-card {
          display: grid;
          grid-template-columns: 36px 100px 1fr;
          gap: 1rem;
          align-items: center;
          background: #131313;
          border: 1px solid #232323;
          border-radius: 12px;
          padding: 0.85rem;
          transition: border-color 0.18s, transform 0.18s;
          cursor: pointer;
        }
        .dense-card:hover {
          border-color: rgba(230, 47, 136, 0.45);
          transform: translateY(-1px);
        }
        .dense-card:focus-visible {
          outline: 2px solid #E62F88;
          outline-offset: 2px;
        }
        .rank {
          color: #6f6f78;
          font-size: 1rem;
          font-weight: 800;
          letter-spacing: 0.04em;
          text-align: center;
        }
        .poster {
          aspect-ratio: 2 / 3;
          width: 100px;
          border-radius: 8px;
          overflow: hidden;
          background: #0a0a0a;
        }
        .poster img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .poster-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #555;
          font-size: 0.7rem;
        }
        .body {
          min-width: 0;
        }
        .title {
          color: #fff;
          font-size: 1.05rem;
          font-weight: 700;
          line-height: 1.2;
          margin: 0 0 0.3rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .meta {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          color: #a1a1aa;
          font-size: 0.78rem;
          margin-bottom: 0.4rem;
        }
        .dense-reason {
          margin-bottom: 0.45rem;
        }
        .dot { color: #3a3a3a; }
        .score {
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
          color: #facc15;
          font-weight: 700;
        }
        .dense-overview {
          color: #a1a1aa;
          font-size: 0.85rem;
          line-height: 1.45;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .providers {
          display: flex;
          gap: 0.3rem;
          margin-top: 0.55rem;
        }
        .provider {
          display: inline-flex;
          padding: 2px;
          background: #0f0f0f;
          border: 1px solid #232323;
          border-radius: 5px;
        }
        .provider img { display: block; border-radius: 3px; }
        .provider-text {
          color: #d4d4d4;
          font-size: 0.65rem;
          padding: 0 0.3rem;
          font-weight: 600;
        }
        @media (max-width: 720px) {
          .dense-card {
            grid-template-columns: 28px 80px 1fr;
            gap: 0.7rem;
          }
          .poster { width: 80px; }
        }
      `}</style>
    </article>
  );
};

export default ShowDenseCard;
